/**
 * useHashTabNavigation - Hook for URL query param-based tab navigation
 *
 * Syncs tab state with URL query param (?tab=X) for browser back/forward support.
 * Handles bidirectional sync between form tabs and sidebar navigation.
 *
 * @module hooks/useHashTabNavigation
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Tab name to index mapping
 */
const TAB_NAME_MAP = {
    0: 'site',
    1: 'utility',
    2: 'load',
    3: 'design',
    4: 'financial',
    5: 'variants',
    6: 'results'
};

/**
 * Tab name to index mapping (includes backwards compat aliases)
 */
const NAME_TAB_MAP = {
    'site': 0,
    'utility': 1,
    'load': 2,
    'design': 3,
    'financial': 4,
    'variants': 5,
    'analysis': 5, // backwards compat
    'results': 6,
    'performance': 6, // backwards compat
};

/**
 * @typedef {Object} HashTabNavigationOptions
 * @property {number} currentTab - Current tab index from form state
 * @property {Function} setTab - Function to update tab in form state
 * @property {number} sidebarTab - Current tab from sidebar/context
 * @property {Function} setSidebarTab - Function to update sidebar tab in context
 */

/**
 * @typedef {Object} HashTabNavigationReturn
 * @property {React.MutableRefObject<boolean>} hasInitializedFromHash - Ref tracking if initialized from URL hash
 */

/**
 * Hook for managing hash-based tab navigation
 *
 * Provides:
 * 1. Initial tab state from URL hash on mount
 * 2. Browser back/forward support via popstate
 * 3. URL hash updates when tab changes
 * 4. Bidirectional sync between form tabs and sidebar
 *
 * @param {HashTabNavigationOptions} options
 * @returns {HashTabNavigationReturn}
 */
export function useHashTabNavigation({
    currentTab,
    setTab,
    sidebarTab,
    setSidebarTab,
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    // Track if we've initialized tab from URL (to prevent context sync from overriding)
    const hasInitializedFromHash = useRef(false);
    // Track if initial mount is complete (prevents sync effect from running before form state updates)
    const mountCompleteRef = useRef(false);
    // Ref to track previous form tab value for detecting actual form tab changes
    const prevFormTabRef = useRef(currentTab);

    // Read query param on mount and whenever searchParams change
    // Use useLayoutEffect to run synchronously BEFORE any other effects (like sidebar sync)
    useLayoutEffect(() => {
        const tabParam = searchParams.get('tab');
        let tabIndex = 0;

        if (tabParam) {
            // Try parsing as number first
            const numericTab = parseInt(tabParam);
            if (!isNaN(numericTab) && numericTab >= 0 && numericTab <= 6) {
                tabIndex = numericTab;
            } else {
                // Try parsing as tab name
                tabIndex = NAME_TAB_MAP[tabParam] ?? 0;
            }
        }

        if (tabIndex !== 0) {
            hasInitializedFromHash.current = true;
        }

        setTab(tabIndex);
        // Also update sidebar context to prevent sync from overriding
        if (setSidebarTab) {
            setSidebarTab(tabIndex);
        }
        // Mark mount as complete after state updates are queued
        setTimeout(() => {
            mountCompleteRef.current = true;
        }, 0);
    }, [searchParams, setTab, setSidebarTab]); // Re-run when searchParams change

    // Update query param when tab changes
    // IMPORTANT: Only run after mount is complete to avoid race condition where
    // this effect deletes the tab param before form state updates from URL
    useEffect(() => {
        // Skip if mount not complete (prevents deleting tab param before form state updates)
        if (!mountCompleteRef.current) return;

        const currentTabParam = searchParams.get('tab');
        const expectedTabParam = currentTab === 0 ? null : currentTab.toString();

        // CRITICAL: If URL has a tab param and form is 0, DON'T delete the param!
        // This happens when component remounts (e.g., app transition) - trust the URL
        if (currentTab === 0 && currentTabParam) {
            return;
        }

        if (currentTabParam !== expectedTabParam) {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                if (currentTab === 0) {
                    newParams.delete('tab'); // Remove param for default tab
                } else {
                    newParams.set('tab', currentTab.toString());
                }
                return newParams;
            }, { replace: true });
        }
    }, [currentTab, searchParams, setSearchParams]);

    // Sync form tab → sidebar context (when form tab changes from non-sidebar source)
    // This effect should ONLY sync when form tab actually changed,
    // not when sidebarTab changed from sidebar navigation
    useEffect(() => {
        const formTabChanged = prevFormTabRef.current !== currentTab;
        prevFormTabRef.current = currentTab;

        // Only sync to context if the FORM tab specifically changed (not sidebar click)
        if (setSidebarTab && formTabChanged && sidebarTab !== currentTab) {
            setSidebarTab(currentTab);
        }
    }, [currentTab, setSidebarTab, sidebarTab]);

    return {
        hasInitializedFromHash,
    };
}

export default useHashTabNavigation;
